import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import { useAuth } from "@/context/AuthContext";
import { cleanReports } from "@/lib/localityUtils";
import {
  Plus, AlertOctagon, AlertTriangle, MoreHorizontal, Eye, MessageCircle,
  ThumbsUp, Share2, MapPin, LayoutGrid, Loader2, User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

function timeShort(iso, lang) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === "ar" ? "الآن" : lang === "he" ? "עכשיו" : "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}w`;
}

export default function ReportsListPage() {
  const { language } = useI18n();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const filterAuthor = params.get("author"); // "me" | null
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports");
        setReports(cleanReports(data));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = reports;
    if (filterAuthor === "me" && me) list = list.filter((r) => r.author?.id === me.id);
    if (activeFilter === "emergency") list = list.filter((r) => (r.category || "").toLowerCase() === "emergency");
    else if (activeFilter !== "all") list = list.filter((r) => (r.category || "").toLowerCase() === activeFilter);
    return list;
  }, [reports, filterAuthor, activeFilter, me]);

  const totalCount = filtered.length;
  const urgentCount = filtered.filter((r) => (r.category || "").toLowerCase() === "emergency").length;

  // Category chips derived from data
  const categories = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => { if (r.category) set.add(r.category); });
    return Array.from(set);
  }, [reports]);

  const topBar = (
    <div className="px-4 pt-3 pb-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h1 className="font-extrabold text-[22px] text-[var(--te-text)] leading-tight">
            {t("reports_title", language)}
          </h1>
          <p className="text-[12px] text-[var(--te-text-muted)] mt-0.5">
            {t("reports_summary", language, totalCount, urgentCount)}
          </p>
        </div>
        <Link
          to="/report/new"
          data-testid="reports-new-btn"
          className="te-btn-primary !py-2 !px-3 text-[13px]"
        >
          <Plus size={14} /> {t("reports_new_button", language)}
        </Link>
      </div>
    </div>
  );

  return (
    <MobileShell topBar={topBar}>
      {/* Filter chips row */}
      <div
        className="px-4 py-3 flex items-center gap-2 overflow-x-auto te-no-scrollbar border-b border-[var(--te-divider)]"
        data-testid="reports-filter-chips"
      >
        <FilterChip
          active={activeFilter === "all"}
          onClick={() => setActiveFilter("all")}
          testId="reports-filter-all"
        >
          <LayoutGrid size={13} /> {t("reports_filter_all", language)}
        </FilterChip>
        <FilterChip
          active={activeFilter === "emergency"}
          onClick={() => setActiveFilter("emergency")}
          testId="reports-filter-emergency"
        >
          <AlertOctagon size={13} className="text-[var(--te-danger)]" /> Emergency
        </FilterChip>
        {categories.filter((c) => c.toLowerCase() !== "emergency").map((c) => (
          <FilterChip
            key={c}
            active={activeFilter === c}
            onClick={() => setActiveFilter(c)}
            testId={`reports-filter-${c}`}
          >
            <span className="capitalize">{c.replace(/_/g, " ")}</span>
          </FilterChip>
        ))}
      </div>

      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
        ) : filtered.length === 0 ? (
          <div className="te-card p-8 text-center text-sm text-[var(--te-text-secondary)]">
            {language === "ar" ? "لا توجد بلاغات." : language === "he" ? "אין דיווחים." : "No reports."}
          </div>
        ) : (
          filtered.map((r) => (
            <CommunityReportCard
              key={r.id}
              report={r}
              language={language}
              onOpen={() => navigate(`/reports/${r.id}`)}
            />
          ))
        )}
      </div>
    </MobileShell>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold border transition-colors ${
        active
          ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
          : "bg-white text-[var(--te-text)] border-[var(--te-divider)] hover:bg-[var(--te-bg-soft)]"
      }`}
    >
      {children}
    </button>
  );
}

function CommunityReportCard({ report, language, onOpen }) {
  const isEmergency = (report.category || "").toLowerCase() === "emergency";
  const cityLabel = report.author?.city_ar || report.author?.city_he || report.author?.city_en;

  return (
    <article
      data-testid={`report-card-${report.id}`}
      className={`te-card te-card-hover te-fade-up overflow-hidden ${isEmergency ? "border-2 !border-[var(--te-danger)]" : ""}`}
    >
      {isEmergency && (
        <div className="px-4 py-1.5 bg-[var(--te-danger)] text-white text-[11px] font-bold inline-flex items-center gap-1">
          <AlertOctagon size={12} /> {t("emergency", language)}
        </div>
      )}

      <div className="p-4 cursor-pointer" onClick={onOpen}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--te-input-bg)] flex items-center justify-center flex-shrink-0">
            <UserIcon size={18} className="text-[var(--te-text-muted)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-[15px] text-[var(--te-text)] truncate" dir="auto">
                {report.author?.name || "—"}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); }}
                data-testid={`report-menu-${report.id}`}
                className="p-1 -mt-1 -mr-1 rounded-full hover:bg-[var(--te-bg-soft)] text-[var(--te-text-muted)]"
              >
                <MoreHorizontal size={18} />
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--te-text-muted)] mt-0.5 flex-wrap" dir="auto">
              {cityLabel && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={11} /> {cityLabel}
                </span>
              )}
              <span>·</span>
              <span>{timeShort(report.created_at, language)}</span>
              {report.category && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-[10px] ${
                  isEmergency
                    ? "bg-[var(--te-danger-soft)] text-[var(--te-danger)]"
                    : "bg-[var(--te-primary-soft)] text-[var(--te-primary)]"
                }`}>
                  {isEmergency && <AlertOctagon size={10} />}
                  {report.category.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-[#E0F2FE] text-[var(--te-info)] rounded-full text-[10px] font-bold">
              {t("new_label", language)} <span className="w-1.5 h-1.5 rounded-full bg-[var(--te-info)]" />
            </div>
          </div>
        </div>

        <h3 className="font-extrabold text-[18px] text-[var(--te-text)] mt-3 leading-snug" dir="auto">
          {report.title}
        </h3>
        {report.description && (
          <p className="text-[13px] text-[var(--te-text-secondary)] mt-1 line-clamp-2" dir="auto">
            {report.description}
          </p>
        )}
        {report.image_url && (
          <img
            src={report.image_url}
            alt=""
            className="w-full max-h-[260px] object-cover rounded-xl mt-3 border border-[var(--te-divider)]"
          />
        )}

        {/* counts row */}
        <div className="flex items-center justify-end gap-4 mt-3 text-[12px] text-[var(--te-text-muted)]" dir="ltr">
          <span className="inline-flex items-center gap-1 text-[var(--te-primary)] font-semibold">
            <ThumbsUp size={12} /> {report.likes_count || 0}
          </span>
          <span className="inline-flex items-center gap-1 text-[var(--te-info)] font-semibold">
            <MessageCircle size={12} /> {report.comments_count || 0}
          </span>
          <span className="inline-flex items-center gap-1 text-[var(--te-text-muted)] font-semibold">
            <Eye size={12} /> {report.views_count || 8}
          </span>
        </div>
      </div>

      {/* action row */}
      <div className="grid grid-cols-3 border-t border-[var(--te-divider)] divide-x divide-[var(--te-divider)] rtl:divide-x-reverse">
        <ActionBtn icon={ThumbsUp} label={t("support", language)} testId={`report-support-${report.id}`} />
        <ActionBtn icon={MessageCircle} label={t("comment", language)} testId={`report-comment-${report.id}`} onClick={() => onOpen()} />
        <ActionBtn icon={Share2} label={t("share", language)} testId={`report-share-${report.id}`} />
      </div>
    </article>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="flex items-center justify-center gap-2 py-3 text-[13px] font-semibold text-[var(--te-text-secondary)] hover:bg-[var(--te-bg-soft)] transition-colors"
    >
      <Icon size={15} />
      {label}
    </button>
  );
}
